import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { getApiBaseUrl } from '../../utils/apiConfig';

// Types
export interface Account {
  id: number;
  accountNumber: string;
  accountType: string;
  balance: number;
  currency: string;  // Added currency field
}

export interface Transaction {
  id: number;
  type: string;
  amount: number;
  currency: string;  // Added currency field
  transactionDate: string;
  description: string;
  category: string;
  status: string;
}

export interface DashboardData {
  totalBalance: number;
  currentBalance: number;
  blockedBalance: number;
  totalCryptoValue: number;
  totalSavingsValue: number;
  totalAssetsValue: number;
  primaryCurrency: string;  // Added primaryCurrency field
  pendingMultiSigApprovals?: number; // Added for multi-signature wallet approvals
  accounts: Account[];
  recentTransactions: Transaction[];
}

export interface DashboardState {
  data: DashboardData | null;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: DashboardState = {
  data: null,
  isLoading: false,
  error: null,
};

// Async thunk
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchData',
  async (_, { rejectWithValue, getState, dispatch }) => {
    const fetchDashboardOnce = async (): Promise<Response> => {
      const state = getState() as RootState;
      const { token, type: authType } = state.auth;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }

      return fetch(`${getApiBaseUrl()}/dashboard`, {
        method: 'GET',
        headers,
        credentials: 'include', // session cookies; harmless for JWT
      });
    };

    try {
      let response = await fetchDashboardOnce();

      // One silent refresh + retry for expired JWT (avoids false "session expired" on race / short TTL)
      if (response.status === 401) {
        const state = getState() as RootState;
        const canRefresh =
          state.auth.type === 'JWT' && !!state.auth.refreshToken;
        if (canRefresh) {
          try {
            const { refreshToken } = await import('./authSlice');
            const refreshResult = await dispatch(refreshToken());
            if (refreshToken.fulfilled.match(refreshResult)) {
              response = await fetchDashboardOnce();
            }
          } catch (e) {
            console.warn('Dashboard: JWT refresh before retry failed', e);
          }
        }
      }

      if (!response.ok) {
        const json = await response.json();
        const errJoined = Array.isArray(json.errors) ? json.errors.join(', ') : '';
        const errText = `${errJoined} ${json.message || ''}`.trim().toLowerCase();

        // Handle 401 (Unauthorized) — missing/invalid auth only
        if (response.status === 401) {
          return rejectWithValue('Your session has expired. Please login again.');
        }

        // Some gateways or handlers surface permission failures as 400 with "Access Denied"
        const isAccessDenied =
          errText.includes('access denied') ||
          errText.includes('forbidden') ||
          errText.includes('permission denied');

        // Handle 403 (Forbidden) — or 400 that clearly means permission, not session
        if (response.status === 403 || (response.status === 400 && isAccessDenied)) {
          console.warn(
            'Dashboard: Permission or access denied for dashboard aggregate; rendering with limited empty data'
          );

          const existingState = (getState() as RootState).dashboard;
          const existingData = existingState?.data;

          if (existingData) {
            console.log('Dashboard: Preserving existing data on permission-style error');
            return existingData;
          }

          return {
            totalBalance: 0,
            currentBalance: 0,
            blockedBalance: 0,
            totalCryptoValue: 0,
            totalSavingsValue: 0,
            totalAssetsValue: 0,
            primaryCurrency: 'USD',
            accounts: [],
            recentTransactions: []
          };
        }

        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to fetch dashboard data');
      }

      const json = await response.json();
      return json.data;
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      return rejectWithValue('Network error occurred');
    }
  }
);

// Slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
    clearDashboard: (state) => {
      state.data = null;
      state.isLoading = false;
      state.error = null;
    },
    updateDashboardData: (state, action: PayloadAction<Partial<DashboardData>>) => {
      if (state.data) {
        state.data = { ...state.data, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        console.log('Dashboard: Fetch pending');
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        console.log('Dashboard: Fetch fulfilled, data received');
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        console.log('Dashboard: Fetch rejected:', action.payload);
        state.isLoading = false;
        // Keep rendering existing dashboard data if a later refresh fails.
        if (state.data) {
          state.error = null;
          return;
        }
        state.error = action.payload as string;
      });
  },
});

export const { clearDashboardError, clearDashboard, updateDashboardData } = dashboardSlice.actions;
export default dashboardSlice.reducer; 